import _ from '@luna/lodash';
import type { DeepPartial } from '@luna/utils-ts';
import { deepClone, deepFreeze } from '@luna/utils';
import { getEnv } from '@luna/env';

import {
    getGatekeeperDefaultFeatures,
    getGatekeeperFeatures,
    getGatekeeperFeaturesWithoutCustomOverrides,
    setGatekeeperFeatures,
} from './isFeatureEnabled';
import { getFeaturesFromUrl } from './getFeaturesFromUrl';
import { getFeaturesFromStorage } from './featuresStorageService';

const mergeCustomFeatureOverrides = <T>(featuresWithoutCustomOverrides: T): T => {
    if (getEnv()?.loadOverridesFromUrlAndStorage) {
        return _.merge(
            deepClone(featuresWithoutCustomOverrides),
            getFeaturesFromStorage(),
            getFeaturesFromUrl(),
        );
    }

    if (getEnv()?.environment === 'uat' && getEnv()?.allowUatOverrides) {
        return _.merge(deepClone(featuresWithoutCustomOverrides), getFeaturesFromStorage());
    }

    return deepClone(featuresWithoutCustomOverrides);
};

/**
 * For use by application shells only
 * @param featureOverrides
 */
export const addFeaturesOverridesAndLock = <T extends Record<string, unknown>>(
    featureOverrides = {} as DeepPartial<T>,
): T => {
    const defaultFeatures: T = getGatekeeperFeatures() ?? {};
    const featuresWithoutCustomOverrides: T = _.merge(deepClone(defaultFeatures), featureOverrides);
    const features = mergeCustomFeatureOverrides<T>(featuresWithoutCustomOverrides);

    deepFreeze(defaultFeatures);
    deepFreeze(featuresWithoutCustomOverrides);
    deepFreeze(features);

    setGatekeeperFeatures({ features, featuresWithoutCustomOverrides, defaultFeatures });

    return features;
};

/**
 * For use only by tests to modify features because it 'unfreezes' features config
 */
export const addDangerouslyFeaturesOverrides = (
    featureOverrides: Record<string, unknown>,
): void => {
    setGatekeeperFeatures({
        features: deepFreeze(_.merge(deepClone(getGatekeeperFeatures() ?? {}), featureOverrides)),
        featuresWithoutCustomOverrides: getGatekeeperFeaturesWithoutCustomOverrides(),
        defaultFeatures: getGatekeeperDefaultFeatures(),
    });
};
